import { Alerts, Button, Text } from '@components/ui';
import { useUploadRatingVideosMutation } from '@lib/api/rating';
import { uploadFile } from '@lib/api/uploads';
import { logError } from '@lib/logError';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Buffer } from 'buffer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type { VideoLocationState } from './videoUploadTypes';
const browserWindow = window as Window & { Buffer?: typeof Buffer };
browserWindow.Buffer = browserWindow.Buffer || Buffer;

const VideoUpload = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const jobData = (location.state as VideoLocationState).jobData;
  const [videos, setVideos] = useState<string[]>([]);
  const [videosForUpload, setVideosForUpload] = useState<
    Array<File | undefined>
  >([]);
  const [videosUploaded, setVideosUploaded] = useState(0);
  const [loading, setLoading] = useState(false);
  const uploadVideos = useUploadRatingVideosMutation();
  const [showDeleteIcon, setShowDeleteIcon] = useState(true);
  const handleSelectVideos = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = event.target.files as FileList;
    if (files.length != 0) {
      setVideos([...videos, URL.createObjectURL(files[0])]);
    }
    setVideosForUpload([...videosForUpload, files[0]]);
  };

  const handleUploadVideos = async (): Promise<void> => {
    try {
      const tempVideosArray: string[] = [];
      if (videos.length > 0) {
        setShowDeleteIcon(false);
        setLoading(true);
        for (let i = 0; i < videosForUpload.length; i++) {
          setVideosUploaded(i + 1);
          const file = videosForUpload[i];
          if (file) {
            const uploadRes = await uploadFile(
              file,
              `job-ratings-media/${jobData.id}/videos`
            );
            tempVideosArray.push(uploadRes.location);
          }
        }
      } else {
        toast.error('Please upload at least one video');
      }

      uploadVideos
        .mutateAsync({ links: tempVideosArray, jobId: jobData.id })
        .then(() => {
          setLoading(false);
          toast.success('Videos uploaded successfully');
          setTimeout(() => {
            navigate('/special-notes', { state: { jobData: jobData } });
          }, 1000);
        });
    } catch (error: unknown) {
      setLoading(false);
      logError('video upload', error);
      toast.error('Error in uploading files');
    }
  };

  const handleDeleteVideosRes = (index: number): void => {
    const videosArr = [...videos];
    const videosForUploadArr = [...videosForUpload];
    videosArr.splice(index, 1);
    videosForUploadArr.splice(index, 1);
    setVideos(videosArr);
    setVideosForUpload(videosForUploadArr);
  };
  const handleDeleteVideos = (index: number): void => {
    Alerts.deleteVideoAlert(handleDeleteVideosRes, index);
  };
  return (
    <Box sx={{ width: '100%', mt: '30px' }}>
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Text
          as="h2"
          variant="subtitle"
          sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
        >
          {t('rating.uploadVideos')}
        </Text>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6} sm={4} md={3}>
            <Box
              component="button"
              type="button"
              aria-label={t('rating.uploadVideo')}
              sx={{
                width: '100%',
                height: 120,
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
              }}
              onClick={() => {
                const videoField = document.getElementById(
                  'videoField'
                ) as HTMLInputElement;
                videoField.click();
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 48 }} />
            </Box>

            <Box
              component="input"
              type="file"
              accept="video/*"
              onChange={handleSelectVideos}
              id="videoField"
              sx={{ height: 0, width: 0, p: 0, border: 0 }}
            />
          </Grid>

          {videos.map((item, index) => (
            <Grid item key={item} xs={6} sm={4} md={3}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  height: 90,
                  width: '100%',
                  cursor: 'pointer',
                  borderRadius: '10px',
                }}
              >
                {showDeleteIcon && (
                  <Box
                    component="span"
                    onClick={() => handleDeleteVideos(index)}
                    sx={{
                      position: 'absolute',
                      right: '5px',
                      top: '2px',
                      zIndex: 3,
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <HighlightOffIcon
                      sx={{
                        fontSize: 18,
                        textAlign: 'center',
                        bgcolor: 'common.white',
                        borderRadius: '25px',
                      }}
                    />
                  </Box>
                )}

                <ReactPlayer
                  url={item}
                  width="100%"
                  height="120px"
                  playIcon={'play' as unknown as JSX.Element}
                  controls={true}
                  style={{ borderRadius: '10px' }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
        <Button
          variant="primary"
          sx={{ display: 'block', mx: 'auto', my: 2, minWidth: 180 }}
          disabled={videosForUpload.length < 1 || loading == true}
          onClick={() => {
            void handleUploadVideos();
          }}
        >
          {loading
            ? `Uploading ${videosUploaded}/${videosForUpload.length} videos`
            : 'UPLOAD'}
        </Button>
      </Container>
    </Box>
  );
};

export default VideoUpload;
